import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PartnerMember {
  id: string;
  partner_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'support' | 'financeiro';
  created_at: string;
  allowed_modules: string[] | null;
  allowed_org_ids: string[] | null;
}

export interface PartnerBranding {
  logo_light_url?: string | null;
  logo_dark_url?: string | null;
  logo_scale?: number;
  logo_position_x?: number;
  logo_position_y?: number;
  system_name?: string;
  login_title?: string;
  login_subtitle?: string;
  favicon_url?: string;
  login_background_url?: string | null;
  default_theme?: 'light' | 'dark' | 'system';
  welcome_message?: string;
  support_email?: string;
  support_whatsapp?: string;
  support_url?: string;
  domains?: string[];
}

export interface PartnerData {
  id: string;
  name: string;
  slug: string;
  type: string;
  is_active: boolean;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  max_orgs: number;
  plan_type: string;
  branding?: PartnerBranding | null;
  is_founder?: boolean;
}

export function usePartnerRole() {
  // Check if current user is a partner admin by checking partner_members table directly
  const { data: isPartnerAdmin, isLoading: isCheckingPartner } = useQuery({
    queryKey: ["is-partner-admin"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Check if user is in partner_members with any valid partner role
      const { data, error } = await supabase
        .from("partner_members")
        .select("role")
        .eq("user_id", user.id)
        .limit(1);

      if (error) {
        console.error("Error checking partner admin status:", error);
        return false;
      }
      
      // Any valid partner role (owner, admin, support, financeiro) = can access partner portal
      return data && data.length > 0 && ['owner', 'admin', 'support', 'financeiro'].includes(data[0].role);
    },
    staleTime: 60000, // Cache for 1 minute
    gcTime: 300000, // Keep in cache for 5 minutes
  });

  // Get current user's partner ID
  const { data: partnerId, isLoading: isLoadingPartnerId } = useQuery({
    queryKey: ["current-partner-id"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase.rpc('get_user_partner_id', { _user_id: user.id });
      if (error) {
        console.error("Error getting partner ID:", error);
        return null;
      }
      return data;
    },
    enabled: isPartnerAdmin === true,
    staleTime: 60000,
    gcTime: 300000,
  });

  // Get partner data
  const { data: partnerData, isLoading: isLoadingPartner } = useQuery({
    queryKey: ["current-partner-data", partnerId],
    queryFn: async () => {
      if (!partnerId) return null;

      const { data, error } = await supabase
        .from("partners")
        .select("id, name, slug, type, is_active, logo_url, primary_color, secondary_color, max_orgs, plan_type, branding, is_founder")
        .eq("id", partnerId)
        .single();

      if (error) {
        console.error("Error fetching partner data:", error);
        return null;
      }
      return data as PartnerData;
    },
    enabled: !!partnerId,
    staleTime: 60000,
    gcTime: 300000,
  });

  // Get partner member info (role within partner)
  const { data: memberInfo } = useQuery({
    queryKey: ["partner-member-info", partnerId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !partnerId) return null;

      const { data, error } = await supabase
        .from("partner_members")
        .select("id, partner_id, user_id, role, created_at, allowed_modules, allowed_org_ids")
        .eq("partner_id", partnerId)
        .eq("user_id", user.id)
        .single();

      if (error) {
        console.error("Error fetching partner member info:", error);
        return null;
      }
      return data as PartnerMember;
    },
    enabled: !!partnerId,
  });

  const isLoading = isCheckingPartner || isLoadingPartnerId || isLoadingPartner;

  // Role-based permissions
  const role = memberInfo?.role;
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin';

  return {
    isPartnerAdmin: isPartnerAdmin || false,
    partnerId,
    partnerData,
    memberInfo,
    isLoading,
    
    // Role flags
    isPartnerOwner: isOwner,
    isPartnerAdminRole: isAdmin,
    isPartnerSupport: role === 'support',
    
    // Legacy permissions (kept for backwards compatibility)
    canManageMembers: isOwner,
    canCreateOrgs: isOwner || isAdmin,
    canCreateUsers: isOwner || isAdmin,
  };
}
